/**
 * LMS Service - API calls for Learning Management System
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/lms`;

// ============================================
// COURSE SERVICES
// ============================================

export const getCourses = async (params = {}) => {
  const response = await axios.get(`${API}/courses`, { params });
  return response.data;
};

export const getCourse = async (courseId) => {
  const response = await axios.get(`${API}/courses/${courseId}`);
  return response.data;
};

export const createCourse = async (courseData) => {
  const response = await axios.post(`${API}/courses`, courseData);
  return response.data;
};

export const updateCourse = async (courseId, courseData) => {
  const response = await axios.put(`${API}/courses/${courseId}`, courseData);
  return response.data;
};

export const publishCourse = async (courseId) => {
  const response = await axios.post(`${API}/courses/${courseId}/publish`);
  return response.data;
};

export const archiveCourse = async (courseId) => {
  const response = await axios.post(`${API}/courses/${courseId}/archive`);
  return response.data;
};

export const deleteCourse = async (courseId) => {
  const response = await axios.delete(`${API}/courses/${courseId}`);
  return response.data;
};

// ============================================
// MODULE SERVICES
// ============================================

export const addModule = async (courseId, moduleData) => {
  const response = await axios.post(`${API}/courses/${courseId}/modules`, moduleData);
  return response.data;
};

export const updateModule = async (moduleId, moduleData) => {
  const response = await axios.put(`${API}/modules/${moduleId}`, moduleData);
  return response.data;
};

export const reorderModules = async (courseId, moduleOrder) => {
  const response = await axios.put(`${API}/courses/${courseId}/modules/reorder`, { module_order: moduleOrder });
  return response.data;
};

export const deleteModule = async (moduleId) => {
  const response = await axios.delete(`${API}/modules/${moduleId}`);
  return response.data;
};

// ============================================
// TOPIC SERVICES
// ============================================

export const addTopic = async (moduleId, topicData) => {
  const response = await axios.post(`${API}/modules/${moduleId}/topics`, topicData);
  return response.data;
};

export const updateTopic = async (topicId, topicData) => {
  const response = await axios.put(`${API}/topics/${topicId}`, topicData);
  return response.data;
};

export const reorderTopics = async (moduleId, topicOrder) => {
  const response = await axios.put(`${API}/modules/${moduleId}/topics/reorder`, { topic_order: topicOrder });
  return response.data;
};

export const deleteTopic = async (topicId) => {
  const response = await axios.delete(`${API}/topics/${topicId}`);
  return response.data;
};

// ============================================
// BATCH SERVICES
// ============================================

export const getBatches = async (params = {}) => {
  const response = await axios.get(`${API}/batches`, { params });
  return response.data;
};

export const getBatch = async (batchId) => {
  const response = await axios.get(`${API}/batches/${batchId}`);
  return response.data;
};

export const createBatch = async (batchData) => {
  const response = await axios.post(`${API}/batches`, batchData);
  return response.data;
};

export const updateBatch = async (batchId, batchData) => {
  const response = await axios.put(`${API}/batches/${batchId}`, batchData);
  return response.data;
};

export const openBatch = async (batchId) => {
  const response = await axios.post(`${API}/batches/${batchId}/open`);
  return response.data;
};

export const startBatch = async (batchId) => {
  const response = await axios.post(`${API}/batches/${batchId}/start`);
  return response.data;
};

export const completeBatch = async (batchId) => {
  const response = await axios.post(`${API}/batches/${batchId}/complete`);
  return response.data;
};

export const mapTopicsToSessions = async (batchId, mappings) => {
  const response = await axios.put(`${API}/batches/${batchId}/map-topics`, { mappings });
  return response.data;
};

export const getBatchStudents = async (batchId, params = {}) => {
  const response = await axios.get(`${API}/batches/${batchId}/students`, { params });
  return response.data;
};

// ============================================
// SESSION SERVICES
// ============================================

export const getSession = async (sessionId) => {
  const response = await axios.get(`${API}/sessions/${sessionId}`);
  return response.data;
};

export const updateSession = async (sessionId, sessionData) => {
  const response = await axios.put(`${API}/sessions/${sessionId}`, sessionData);
  return response.data;
};

export const updateSessionStatus = async (sessionId, status) => {
  const response = await axios.put(`${API}/sessions/${sessionId}/status`, { status });
  return response.data;
};

export const addSessionRecording = async (sessionId, recordingData) => {
  const response = await axios.post(`${API}/sessions/${sessionId}/recording`, recordingData);
  return response.data;
};

export const addSessionResources = async (sessionId, resources) => {
  const response = await axios.post(`${API}/sessions/${sessionId}/resources`, { resources });
  return response.data;
};

export const removeSessionResource = async (sessionId, resourceIndex) => {
  const response = await axios.delete(`${API}/sessions/${sessionId}/resources/${resourceIndex}`);
  return response.data;
};

// ============================================
// ENROLLMENT SERVICES
// ============================================

export const enrollInCourse = async (enrollmentData) => {
  const response = await axios.post(`${API}/enroll`, enrollmentData);
  return response.data;
};

export const verifyEnrollmentPayment = async (paymentData) => {
  const response = await axios.post(`${API}/enroll/verify-payment`, paymentData);
  return response.data;
};

export const getMyCourses = async (status = null) => {
  const params = status ? { status } : {};
  const response = await axios.get(`${API}/my-courses`, { params });
  return response.data;
};

export const getEnrollment = async (enrollmentId) => {
  const response = await axios.get(`${API}/enrollments/${enrollmentId}`);
  return response.data;
};

export const dropEnrollment = async (enrollmentId, reason) => {
  const response = await axios.post(`${API}/enrollments/${enrollmentId}/drop`, { reason });
  return response.data;
};

export const adminGrantEnrollment = async (grantData) => {
  const response = await axios.post(`${API}/admin/grant-enrollment`, grantData);
  return response.data;
};

// ============================================
// CALENDAR SERVICES
// ============================================

export const getUserCalendar = async (month, year, params = {}) => {
  const response = await axios.get(`${API}/calendar`, { params: { month, year, ...params } });
  return response.data;
};

export const getAdminCalendar = async (month, year, params = {}) => {
  const response = await axios.get(`${API}/admin/calendar`, { params: { month, year, ...params } });
  return response.data;
};

// ============================================
// ATTENDANCE SERVICES
// ============================================

export const markSessionAttendance = async (sessionId, attendees) => {
  const response = await axios.post(`${API}/sessions/${sessionId}/attendance`, { attendees });
  return response.data;
};

export const recordWatchProgress = async (sessionId, progressData) => {
  const response = await axios.post(`${API}/sessions/${sessionId}/watch-progress`, progressData);
  return response.data;
};

export const getSessionAttendance = async (sessionId) => {
  const response = await axios.get(`${API}/sessions/${sessionId}/attendance`);
  return response.data;
};

// ============================================
// CERTIFICATE SERVICES
// ============================================

export const getMyCertificates = async () => {
  const response = await axios.get(`${API}/certificates`);
  return response.data;
};

export const getCertificate = async (certificateId) => {
  const response = await axios.get(`${API}/certificates/${certificateId}`);
  return response.data;
};

export const verifyCertificate = async (certificateNumber) => {
  const response = await axios.get(`${API}/verify/${certificateNumber}`);
  return response.data;
};

export const issueCertificateManual = async (issueData) => {
  const response = await axios.post(`${API}/admin/certificates/issue`, issueData);
  return response.data;
};

export const revokeCertificate = async (certificateId, reason) => {
  const response = await axios.post(`${API}/admin/certificates/${certificateId}/revoke`, { reason });
  return response.data;
};

// ============================================
// INSTRUCTOR SERVICES
// ============================================

export const getInstructorDashboard = async () => {
  const response = await axios.get(`${API}/instructor/dashboard`);
  return response.data;
};

export const getInstructorCourses = async () => {
  const response = await axios.get(`${API}/instructor/courses`);
  return response.data;
};

export const getInstructorBatches = async (params = {}) => {
  const response = await axios.get(`${API}/instructor/batches`, { params });
  return response.data;
};

export default {
  // Courses
  getCourses,
  getCourse,
  createCourse,
  updateCourse,
  publishCourse,
  archiveCourse,
  deleteCourse,
  // Modules
  addModule,
  updateModule,
  reorderModules,
  deleteModule,
  // Topics
  addTopic,
  updateTopic,
  reorderTopics,
  deleteTopic,
  // Batches
  getBatches,
  getBatch,
  createBatch,
  updateBatch,
  openBatch,
  startBatch,
  completeBatch,
  mapTopicsToSessions,
  getBatchStudents,
  // Sessions
  getSession,
  updateSession,
  updateSessionStatus,
  addSessionRecording,
  addSessionResources,
  removeSessionResource,
  // Enrollments
  enrollInCourse,
  verifyEnrollmentPayment,
  getMyCourses,
  getEnrollment,
  dropEnrollment,
  adminGrantEnrollment,
  // Calendar
  getUserCalendar,
  getAdminCalendar,
  // Attendance
  markSessionAttendance,
  recordWatchProgress,
  getSessionAttendance,
  // Certificates
  getMyCertificates,
  getCertificate,
  verifyCertificate,
  issueCertificateManual,
  revokeCertificate,
  // Instructor
  getInstructorDashboard,
  getInstructorCourses,
  getInstructorBatches
};
